import { useCallback, useState } from 'react';
import { useSetAtom } from 'jotai';
import {
  ariesMdbExportBaseAtom,
  ariesMdbExportDefaultsBaseAtom,
} from './atoms';
import type {
  AriesMdbExportConfig,
  AriesMdbExportDefaults,
  AriesMdbKickoffResponse,
} from 'components/aries-mdb-export/types';

// ---------------------------------------------------------------------------
// Static fallback defaults (INS-10553)
//
// Used before the `/new` fetch resolves and when it fails, so a slow or failed
// options call never blocks the user (AC6). The D4/D5 business constants are
// mirrored here; the two server-derived values (`forecastDurationMonths`,
// `longestPdpMonths`) fall back to the pipeline default (360) / null (no
// too-short warning), and `referenceDate` is left blank for the user to fill
// (it derives from the eval's effective date, which only the server has). `outputName`
// is blank in the fallback (the server computes the Eval3ExportFilename stem),
// which keeps Export disabled until the user names the output.
// ---------------------------------------------------------------------------
export const ARIES_MDB_FALLBACK_DEFAULTS: AriesMdbExportDefaults = {
  outputName: '',
  template: {
    currentDefaultId: null,
    currentDefaultName: null,
    attachmentHref: null,
  },
  referenceDate: null,
  baseDate: '01/2012',
  forecastDurationMonths: 360,
  longestPdpMonths: null,
  qualifier: 'SIM',
  sidefileName: 'SIM_STRIP',
  typecurveLookupName: 'SIM_TC',
  includeCapex: true,
  gasOpexCorrection: true,
};

interface UseAriesMdbExportReturn {
  /** The config-modal defaults (server-derived, or the static fallback). */
  defaults: AriesMdbExportDefaults;
  /** True while the `/new` options fetch is in flight. */
  isLoadingDefaults: boolean;
  /** True while the kickoff POST is in flight. */
  isSubmitting: boolean;
  /** A generic error to surface inline in the modal (defaults load or kickoff). */
  error: string | null;
  /** Fetches `/new` defaults; on failure keeps the static fallback + sets error. */
  loadDefaults: () => Promise<void>;
  /**
   * POSTs the edited config to the kickoff. Resolves to the kickoff response on
   * `201` (the caller hands its notifications to `useAriesMdbProgress`), or
   * `null` on any error (the modal stays open with the inline error set).
   */
  submit: (
    config: AriesMdbExportConfig
  ) => Promise<AriesMdbKickoffResponse | null>;
  /** Clears the inline error (e.g. on modal close/reopen). */
  clearError: () => void;
}

/**
 * Owns the Aries MDB export config modal's data flow: fetches the `/new`
 * defaults on open (with a static fallback so a slow/failed fetch never blocks
 * the user), and submits the edited config to the S0 kickoff endpoint. Progress
 * is handled separately by `useAriesMdbProgress`; on a `201` the caller feeds
 * this hook's returned notifications into that hook.
 *
 * When `restoredConfig` is supplied (a re-run of the selected calc's restored
 * run), its editable field values overlay the resolved defaults so the modal
 * opens prefilled with the last run's config. The two fields it can't carry —
 * `template` and `longestPdpMonths` (which powers the "forecast too short"
 * warning) — still come from the fresh `/new` fetch.
 *
 * @param {number | null} calculationId - The selected calculation id.
 * @param {AriesMdbExportConfig | null} [restoredConfig] - Last run's config to prefill, or null.
 * @return {UseAriesMdbExportReturn} Defaults, loading/error state, and actions.
 */
export const useAriesMdbExport = (
  calculationId: number | null,
  restoredConfig: AriesMdbExportConfig | null = null
): UseAriesMdbExportReturn => {
  const fetchDefaults = useSetAtom(ariesMdbExportDefaultsBaseAtom);
  const kickoffExport = useSetAtom(ariesMdbExportBaseAtom);

  const [defaults, setDefaults] = useState<AriesMdbExportDefaults>(
    ARIES_MDB_FALLBACK_DEFAULTS
  );
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  // Overlay the restored run's editable values onto whatever defaults resolved,
  // keeping `template` + `longestPdpMonths` from the base (the config can't
  // carry them). A null field falls back to the base default.
  const applyRestoredConfig = useCallback(
    (base: AriesMdbExportDefaults): AriesMdbExportDefaults => {
      if (!restoredConfig) return base;
      return {
        ...base,
        outputName: restoredConfig.outputName || base.outputName,
        referenceDate: restoredConfig.referenceDate ?? base.referenceDate,
        baseDate: restoredConfig.baseDate ?? base.baseDate,
        forecastDurationMonths:
          restoredConfig.forecastDurationMonths ?? base.forecastDurationMonths,
        qualifier: restoredConfig.qualifier ?? base.qualifier,
        sidefileName: restoredConfig.sidefileName ?? base.sidefileName,
        typecurveLookupName:
          restoredConfig.typecurveLookupName ?? base.typecurveLookupName,
        // Booleans are always populated by `create_for_calculation`, but a run
        // persisted without `input` would round-trip as `undefined` — fall back
        // to the base default so the controlled checkbox never breaks.
        includeCapex: restoredConfig.includeCapex ?? base.includeCapex,
        gasOpexCorrection:
          restoredConfig.gasOpexCorrection ?? base.gasOpexCorrection,
      };
    },
    [restoredConfig]
  );

  const loadDefaults = useCallback(async () => {
    if (!calculationId) return;
    setIsLoadingDefaults(true);
    setError(null);
    // Reset to the static fallback (prefilled with the restored config when
    // present) so a re-open doesn't show a prior run's server defaults while the
    // fresh fetch is in flight.
    setDefaults(applyRestoredConfig(ARIES_MDB_FALLBACK_DEFAULTS));
    try {
      const data = await fetchDefaults(calculationId);
      setDefaults(applyRestoredConfig(data));
    } catch (err) {
      // Non-blocking: keep the static fallback and surface a soft error. The
      // user can still edit + Export (AC6).
      setError(
        err instanceof Error
          ? err.message
          : 'Could not load export defaults; using standard defaults.'
      );
    } finally {
      setIsLoadingDefaults(false);
    }
  }, [calculationId, fetchDefaults, applyRestoredConfig]);

  const submit = useCallback(
    async (
      config: AriesMdbExportConfig
    ): Promise<AriesMdbKickoffResponse | null> => {
      if (!calculationId) return null;
      setIsSubmitting(true);
      setError(null);
      try {
        const response = await kickoffExport(calculationId, config);
        if (!response.resources?.notifications?.channel) {
          throw new Error('Aries MDB export kickoff returned no channel');
        }
        return response;
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to start Aries MDB export'
        );
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [calculationId, kickoffExport]
  );

  return {
    defaults,
    isLoadingDefaults,
    isSubmitting,
    error,
    loadDefaults,
    submit,
    clearError,
  };
};
